import {
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Form, Table, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import React from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import styled from 'styled-components';

import useTable from '../../components/Template/TableTemplate';
import { REGIONS } from '../../constants/REGIONS';
import { downloadFile, fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';
import QuotationCreate from './QuotationCreate';
import { formatQuotation } from './utils';

const columns: (regions: string[]) => ColumnProps<any>[] = regions => [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 20,
    sorter: true,
  },
  {
    title: 'Reference Number',
    width: 50,
    dataIndex: 'reference_number',
    key: 'reference_number',
  },
  {
    title: 'Buyer Name',
    dataIndex: 'buyer_name',
    key: 'buyer_name',
    width: 50,
    sorter: true,
  },
  {
    title: 'Buyer Company Name',
    dataIndex: 'buyer_company_name',
    key: 'buyer_company_name',
    width: 50,
    sorter: true,
  },
  {
    title: 'Issue Date',
    width: 80,
    render: record => formatUTCTimeToLocal(record.issue_date, 'DD-MM-YYYY'),
  },
  {
    title: 'Region',
    dataIndex: 'currency',
    key: 'currency',
    width: 30,
    filters: regions.map(item => ({ text: item.trim(), value: item.trim() })),
    onFilter: (value, record) =>
      record.currency.indexOf(value.toString()) === 0,
    render: text => {
      const key = Object.keys(REGIONS).find(k => REGIONS[k] === text);
      return `${key} (${REGIONS[key]})`;
    },
  },
  {
    title: 'Subtotal',
    dataIndex: 'subtotal',
    key: 'subtotal',
    width: 50,
    sorter: true,
  },
  {
    title: 'Tax',
    dataIndex: 'subtax',
    key: 'tax',
    width: 50,
    sorter: true,
    render: (text, record) => {
      return (
        <span>
          {text} {record.tax_rate ? `(${record.tax_rate}%)` : ''}
        </span>
      );
    },
  },
  {
    title: 'Total',
    dataIndex: 'total',
    key: 'total',
    width: 50,
    sorter: true,
  },
  {
    title: 'Created At',
    key: 'created_at',
    render: record =>
      formatUTCTimeToLocal(record.created_at, 'DD-MM-YYYY HH:mm'),
    width: 50,
    sorter: true,
  },
  {
    title: 'Created By',
    render: record => record.user.full_name,
    key: 'user.full_name',
    width: 50,
  },
];
const DownloadQuotation = ({ record }) => {
  const [loading, toggleLoading] = useToggle(false);
  return (
    <Tooltip title={'Download quotation'}>
      <Button
        loading={loading}
        shape="circle"
        icon={<DownloadOutlined />}
        onClick={async () => {
          toggleLoading();
          const blob = await fetchAPI(`quotations/${record.id}/gen-pdf`);
          downloadFile(blob, record.reference_number);
          toggleLoading();
        }}
      />
    </Tooltip>
  );
};
export default () => {
  const user = useSelector((state: AppState) => state.user);
  const [visible, toggle] = useToggle(false);
  const expandedRowRender = record => {
    const productColumns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 150,
        render: (text, item) => (
          <>
            <div>{text}</div>
            <div className="item-description">{item.description}</div>
          </>
        ),
      },
      { title: 'Unit Price', dataIndex: 'unit_price', width: 50 },
      { title: 'Quantity', dataIndex: 'quantity', width: 50 },
      { title: 'Total', dataIndex: 'total', width: 50 },
    ];

    return (
      <Table
        columns={productColumns}
        dataSource={record.lines}
        pagination={false}
      />
    );
  };

  // const [table, toggleLoad, data] = useTable({
  const [table, toggleLoad] = useTable({
    expandToggle: true,
    paginationPosition: 'bottom',
    showDateRange: false,
    showDownload: false,
    url: 'quotations/list',
    tableProps: {
      expandable: {
        expandedRowRender,
        rowExpandable: record => !!record.lines.length,
      },
    },
    tableContentStyle: {
      maxHeight: 'calc(100vh - 180px)',
      overflow: 'auto',
    },
    extra: (
      <Button
        icon={<PlusOutlined />}
        onClick={() => {
          form.setFieldsValue(formatQuotation(null));
          toggle();
        }}
      >
        Create Quotation
      </Button>
    ),
    columns: [
      ...columns(user.regions || []),
      {
        title: 'Actions',
        render: record => (
          <StyledDiv>
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => {
                form.setFieldsValue(formatQuotation(record));
                toggle();
              }}
            />
            <DownloadQuotation record={record} />
          </StyledDiv>
        ),
        width: 50,
        fixed: 'right',
      },
    ],
  });

  const [form] = Form.useForm();
  return (
    <StyledContainer>
      <Drawer
        title={
          <div>
            <span>Create Quotation</span>
            <span style={{ position: 'absolute', right: '20px', top: '10px' }}>
              <Button onClick={form.submit} type="primary">
                Save Quotation
              </Button>
            </span>
          </div>
        }
        placement="right"
        closable={false}
        onClose={() => {
          form.resetFields();
          toggle();
        }}
        visible={visible}
        width={800}
      >
        <QuotationCreate
          form={form}
          onOk={() => {
            toggle();
            toggleLoad();
          }}
          regions={user.regions || []}
          visible={visible}
        />
      </Drawer>
      {table}
    </StyledContainer>
  );
};
const StyledContainer = styled.div`
  .ant-table-tbody > tr .ant-table {
    margin: 10px !important;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #e0e0f8;
  }
  .item-description {
    color: #7e7d7d;
  }
`;
const StyledDiv = styled.div`
  button,
  a {
    margin: 5px;
  }
`;
